/**
 * Phantom Connect Browser SDK — production (poker.grudge-studio.com)
 *
 * Official template:
 *   https://solana.com/developers/templates/phantom-embedded-js
 *   `npx create-solana-dapp@latest <app> --template phantom-embedded-js`
 *   source: solana-foundation/templates/community/phantom-embedded-js
 *
 * Docs: https://docs.phantom.com/sdks/browser-sdk
 * Portal App ID: 8de264e8-1323-4b99-afec-fc13f3e060c7
 *
 * Product rules:
 * - Solana mainnet only (GBUX play money)
 * - providers: injected + google + apple (template is google/apple only; we add extension)
 * - Resume Phantom Connect session on every route (7-day SDK session).
 *   First connect is still an explicit CTA. No second login per page.
 * - Never store private keys; SDK holds session
 */
import {
  BrowserSDK,
  AddressType,
  type ConnectResult,
  type WalletAddress,
} from "@phantom/browser-sdk";
import type { Transaction } from "@solana/web3.js";
import type { ConnectedSolanaWallet, SolanaProvider } from "./solana-wallets";

export type PhantomConnectMode = "injected" | "google" | "apple";

/** Phantom Portal App ID — SSOT (override with VITE_PHANTOM_APP_ID) */
export const PHANTOM_APP_ID =
  (
    (import.meta as { env?: { VITE_PHANTOM_APP_ID?: string } }).env
      ?.VITE_PHANTOM_APP_ID || "8de264e8-1323-4b99-afec-fc13f3e060c7"
  ).trim();

/**
 * Must exactly match a Phantom Portal **Redirect URL** entry.
 * Template uses VITE_REDIRECT_URL; we accept that alias + VITE_PHANTOM_REDIRECT_URL.
 * No wildcards. Prefer /connect (OAuth return).
 */
export const PHANTOM_REDIRECT_URL =
  (
    (import.meta as { env?: { VITE_PHANTOM_REDIRECT_URL?: string; VITE_REDIRECT_URL?: string } })
      .env?.VITE_PHANTOM_REDIRECT_URL ||
    (import.meta as { env?: { VITE_REDIRECT_URL?: string } }).env?.VITE_REDIRECT_URL ||
    "https://poker.grudge-studio.com/connect"
  )
    .trim()
    .replace(/\*$/, "");

export const PHANTOM_AUTH_URL = "https://connect.phantom.app/login";

let sdkSingleton: BrowserSDK | null = null;
let lastConnect: ConnectResult | null = null;
let connectedAddress: string | null = null;

/**
 * Production SDK config — template + extension.
 * Template: providers google/apple only.
 * Poker: also injected for desktop extension users.
 */
export function createPhantomSdk(): BrowserSDK {
  if (!PHANTOM_APP_ID || PHANTOM_APP_ID === "your-app-id-here") {
    throw new Error(
      "Missing Phantom App ID. Set VITE_PHANTOM_APP_ID. Get one at https://phantom.com/portal",
    );
  }
  if (!PHANTOM_REDIRECT_URL) {
    throw new Error(
      "Missing redirect URL. Set VITE_PHANTOM_REDIRECT_URL or VITE_REDIRECT_URL.",
    );
  }
  return new BrowserSDK({
    providers: ["injected", "google", "apple"],
    addressTypes: [AddressType.solana],
    appId: PHANTOM_APP_ID,
    authOptions: {
      authUrl: PHANTOM_AUTH_URL,
      redirectUrl: PHANTOM_REDIRECT_URL,
    },
    // Resume existing Phantom Connect session across lobby/account/slots.
    autoConnect: true,
  });
}

export function getPhantomSdk(): BrowserSDK {
  if (!sdkSingleton) sdkSingleton = createPhantomSdk();
  return sdkSingleton;
}

/** Alias matching template initializeSDK() */
export function initializeSDK(): BrowserSDK {
  return getPhantomSdk();
}

export function resetPhantomSdk(): void {
  sdkSingleton = null;
  lastConnect = null;
  connectedAddress = null;
}

export async function isPhantomExtensionAvailable(
  timeoutMs = 2500,
): Promise<boolean> {
  try {
    const mod = await import("@phantom/browser-sdk");
    const wait = (
      mod as { waitForPhantomExtension?: (t?: number) => Promise<boolean> }
    ).waitForPhantomExtension;
    if (typeof wait === "function") return wait(timeoutMs);
  } catch {
    /* ignore */
  }
  return Boolean(
    typeof window !== "undefined" &&
      (window.phantom?.solana ||
        (window.solana as { isPhantom?: boolean })?.isPhantom),
  );
}

/** Template-style Solana address extract (AddressType.solana preferred) */
export function solanaAddressFrom(result: ConnectResult): string | null {
  const addrs = (result.addresses || []) as WalletAddress[];
  const typed = addrs.find(
    (a) =>
      (a as { addressType?: string }).addressType === AddressType.solana ||
      String((a as { addressType?: string }).addressType || "")
        .toLowerCase()
        .includes("sol"),
  );
  if (typed?.address && typed.address.length >= 32) return typed.address;

  for (const a of addrs) {
    const typeStr = [
      (a as { addressType?: string }).addressType,
      (a as { type?: string }).type,
      (a as { chain?: string }).chain,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const addr = (a as { address?: string }).address;
    if (addr && addr.length >= 32) {
      if (!typeStr || typeStr.includes("sol")) return addr;
    }
  }
  const first = addrs[0] as { address?: string } | undefined;
  if (first?.address && first.address.length >= 32) return first.address;
  return null;
}

export function getAddress(): string | null {
  return connectedAddress;
}

export function isConnected(): boolean {
  try {
    return getPhantomSdk().isConnected();
  } catch {
    return false;
  }
}

function looksLikeOAuthCallback(): boolean {
  if (typeof window === "undefined") return false;
  const urlParams = new URLSearchParams(window.location.search);
  return (
    urlParams.has("response_type") ||
    urlParams.has("wallet_id") ||
    urlParams.has("code") ||
    urlParams.has("state") ||
    sessionStorage.getItem("phantom_oauth_pending") === "1"
  );
}

function cleanOAuthUrl(): void {
  try {
    window.history.replaceState({}, document.title, window.location.pathname);
    sessionStorage.removeItem("phantom_oauth_pending");
  } catch {
    /* ignore */
  }
}

/**
 * Template checkExistingSession — OAuth callback + restore active SDK session.
 * Call from WalletProvider on every route so lobby/account/slots keep the same signer.
 */
export async function checkExistingSession(): Promise<string | null> {
  const sdk = getPhantomSdk();

  if (looksLikeOAuthCallback()) {
    await new Promise((r) => setTimeout(r, 1200));
    try {
      // Template: connect({ provider: 'phantom' }) completes OAuth handshake
      // SDK accepts "phantom" for session resume after redirect (not in our TS mode union)
      const result = await sdk.connect({
        provider: "phantom" as never,
      });
      lastConnect = result;
      let address = solanaAddressFrom(result);
      if (!address && sdk.solana.publicKey) address = sdk.solana.publicKey;
      if (address && address.length >= 32) {
        connectedAddress = address;
        cleanOAuthUrl();
        try {
          await sdk.solana.switchNetwork("mainnet");
        } catch {
          /* already mainnet */
        }
        return address;
      }
    } catch (err) {
      console.warn("[phantom] OAuth callback incomplete", err);
      cleanOAuthUrl();
      return null;
    }
  }

  if (!sdk.isConnected()) return null;

  try {
    const result = await sdk.connect({
      provider: "phantom" as never,
    });
    lastConnect = result;
    let address = solanaAddressFrom(result);
    if (!address && sdk.solana.publicKey) address = sdk.solana.publicKey;
    if (address && address.length >= 32) {
      connectedAddress = address;
      return address;
    }
  } catch {
    /* no restorable session */
  }
  return null;
}

/** Explicit OAuth callback handler (template handleAuthCallback) */
export async function handleAuthCallback(): Promise<string | null> {
  if (!looksLikeOAuthCallback()) return null;
  return checkExistingSession();
}

/**
 * Connect Phantom (SDK).
 * Prefer injected when extension present; else google/apple embedded OAuth.
 * OAuth may redirect — throws REDIRECT_IN_PROGRESS (template pattern).
 */
export async function connectWithPhantomSdk(
  mode: PhantomConnectMode = "injected",
): Promise<ConnectedSolanaWallet> {
  // Puter iframe: OAuth + extension inject need top-level poker host (Portal allowlist)
  try {
    const { ensureTopLevelForPhantomOAuth, isFramed } = await import(
      "@/lib/puter-embed"
    );
    if (mode === "google" || mode === "apple") {
      if (ensureTopLevelForPhantomOAuth()) {
        throw new Error("REDIRECT_IN_PROGRESS");
      }
    } else if (mode === "injected" && isFramed()) {
      const hasExt = await isPhantomExtensionAvailable(1200);
      if (!hasExt) {
        // No inject in iframe — open top-level for Google embed path
        if (ensureTopLevelForPhantomOAuth()) {
          throw new Error("REDIRECT_IN_PROGRESS");
        }
      }
    }
  } catch (e) {
    if (e instanceof Error && e.message === "REDIRECT_IN_PROGRESS") throw e;
    /* puter-embed optional */
  }

  const sdk = getPhantomSdk();

  let provider: PhantomConnectMode = mode;
  if (mode === "injected") {
    const hasExt = await isPhantomExtensionAvailable(2000);
    if (!hasExt) {
      provider = "google";
    }
  }

  if (provider === "google" || provider === "apple") {
    try {
      sessionStorage.setItem("phantom_oauth_pending", "1");
    } catch {
      /* ignore */
    }
  }

  try {
    const result = await sdk.connect({ provider });
    lastConnect = result;

    try {
      await sdk.solana.switchNetwork("mainnet");
    } catch {
      /* injected may already be mainnet */
    }

    let publicKey = solanaAddressFrom(result);
    if (!publicKey && sdk.solana.publicKey) {
      publicKey = sdk.solana.publicKey;
    }
    if (!publicKey || publicKey.length < 32) {
      // Template: OAuth redirect in progress — no address yet
      throw new Error("REDIRECT_IN_PROGRESS");
    }
    connectedAddress = publicKey;
    try {
      sessionStorage.removeItem("phantom_oauth_pending");
    } catch {
      /* ignore */
    }
    return wrapSdkAsConnected(sdk, publicKey, provider);
  } catch (error) {
    if (error instanceof Error && error.message === "REDIRECT_IN_PROGRESS") {
      throw error;
    }
    // OAuth redirect often throws — surface as redirect if pending
    if (
      (provider === "google" || provider === "apple") &&
      typeof window !== "undefined" &&
      sessionStorage.getItem("phantom_oauth_pending") === "1"
    ) {
      throw new Error("REDIRECT_IN_PROGRESS");
    }
    throw error instanceof Error
      ? error
      : new Error("Failed to connect with Phantom");
  }
}

function wrapSdkAsConnected(
  sdk: BrowserSDK,
  publicKey: string,
  mode: PhantomConnectMode = "injected",
): ConnectedSolanaWallet {
  const provider: SolanaProvider = {
    isPhantom: true,
    publicKey: { toString: () => publicKey },
    connect: async () => ({ publicKey: { toString: () => publicKey } }),
    disconnect: async () => {
      await sdk.disconnect();
      lastConnect = null;
      connectedAddress = null;
    },
    signMessage: async (message: Uint8Array) => {
      const result = await sdk.solana.signMessage(message);
      if (result?.signature instanceof Uint8Array) return result.signature;
      throw new Error("Unexpected Phantom signMessage result");
    },
    signAndSendTransaction: async (tx: unknown, opts?: {
      presignTransaction?: (
        t: string,
        ctx: { networkId: string; walletId: string },
      ) => Promise<string>;
    }) => {
      const result = await sdk.solana.signAndSendTransaction(
        tx as Transaction,
        opts?.presignTransaction
          ? { presignTransaction: opts.presignTransaction }
          : undefined,
      );
      if (!result?.signature) {
        throw new Error("Phantom did not return tx signature");
      }
      return { signature: result.signature };
    },
    signTransaction: async (tx: unknown) => {
      return (await sdk.solana.signTransaction(
        tx as Transaction,
      )) as Transaction;
    },
  };

  const label =
    mode === "google"
      ? "Phantom (Google)"
      : mode === "apple"
        ? "Phantom (Apple)"
        : "Phantom";

  return {
    id: "phantom",
    name: label,
    publicKey,
    provider,
  };
}

/**
 * After OAuth return or session restore — bind as ConnectedSolanaWallet for poker context.
 */
export async function resumePhantomAsConnected(): Promise<ConnectedSolanaWallet | null> {
  const address = await checkExistingSession();
  if (!address) return null;
  const sdk = getPhantomSdk();
  return wrapSdkAsConnected(sdk, address, "google");
}

export async function disconnectPhantomSdk(): Promise<void> {
  if (!sdkSingleton) return;
  try {
    await sdkSingleton.disconnect();
  } catch {
    /* ignore */
  }
  lastConnect = null;
  connectedAddress = null;
}

export function getLastPhantomConnect(): ConnectResult | null {
  return lastConnect;
}

export const PHANTOM_SSOT = {
  package: "@phantom/browser-sdk",
  packageVersion: "^2.0.2",
  /** Official Solana developer template page */
  templateUrl: "https://solana.com/developers/templates/phantom-embedded-js",
  template:
    "solana-foundation/templates/community/phantom-embedded-js",
  templateCmd:
    "npx create-solana-dapp@latest <app> --template phantom-embedded-js",
  docs: "https://docs.phantom.com/sdks/browser-sdk",
  docsOverview: "https://docs.phantom.com/wallet-sdks-overview",
  portalUrlsDocs: "https://docs.phantom.com/phantom-portal/configure-urls",
  portal: "https://phantom.com/portal",
  mcpServerDocs: "https://docs.phantom.com/phantom-mcp-server",
  mcpServerPackage: "@phantom/mcp-server",
  appId: PHANTOM_APP_ID,
  domain: "https://poker.grudge-studio.com",
  redirectUrl: PHANTOM_REDIRECT_URL,
  authUrl: PHANTOM_AUTH_URL,
  network: "mainnet" as const,
  providers: ["injected", "google", "apple"] as const,
  addressTypes: ["solana"] as const,
  legalTos: "/tos",
  legalPrivacy: "/privacy",
  /**
   * Phantom Portal Allowed Origins — page origin of the SPA.
   * Puter loads index_url as iframe of poker host → origin stays poker (no puter.com origin needed for OAuth).
   * Add puter.com only if you ever host a second OAuth entry page on Puter.
   */
  allowedOrigins: [
    "https://poker.grudge-studio.com",
    "http://localhost:5173",
    "http://127.0.0.1:5173",
  ] as const,
  /** Puter may frame SPA — CSP frame-ancestors only (not Phantom Portal origins). */
  puterFrameAncestors: [
    "https://puter.com",
    "https://*.puter.com",
    "https://puter.grudge-studio.com",
  ] as const,
  puterApp: {
    name: "grudge-poker",
    uid: "app-80b4bbba-febe-47a4-b22f-344b5b19be02",
    url: "https://puter.com/app/grudge-poker",
    indexUrl: "https://poker.grudge-studio.com/",
  } as const,
  redirectUrls: [
    "https://poker.grudge-studio.com/connect",
    "https://poker.grudge-studio.com/",
    "http://localhost:5173/connect",
    "http://127.0.0.1:5173/connect",
  ] as const,
  /**
   * Never store: private keys, seed, Phantom refresh tokens in localStorage.
   * OK to store: public addresses, labels, last-seen, cached balance display.
   */
  storagePolicy: {
    clientRegistry: "public addresses + labels only",
    playLedger: "edge D1 per address",
    fleetBag: "Railway + Grudge ID JWT",
    signing: "SDK session / injected provider only",
  },
  deployChecklist: [
    "portal-app-id",
    "domain-txt",
    "allowed-origins",
    "redirect-urls-exact",
    "solana-mainnet",
    "no-auto-connect-on-load",
    "oauth-callback-/connect",
    "opt-in-sign",
    "cf-worker-assets-redeploy",
  ] as const,
} as const;
